import type { Doc } from "building-types/doc";
import { wallPlaneAssemblyNew, type WallPlaneAssemblyEntity } from "building-types/walls2/wallPlaneAssemblyTypes";
import type { Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { default2x4Id, default2x6Id, default2x8Id } from "../elements/framing/defaultLumberComponents";
import {
  defaultWallExteriorSheathingComponentId,
  defaultWallInteriorSheathingComponentId,
  defaultWallMasonryComponentId,
  defaultWallCIPStripFootingComponentId,
} from "./assemblyComponentDefaults";

export const defaultWallPlaneEmptyAssemblyId = "DEFAULT-WALL-PLANE-EMPTY-ASSEMBLY";
export const defaultWallPlaneCoreAssemblyId = "DEFAULT-WALL-PLANE-ASSEMBLY";
export const alternateWallPlaneCoreAssemblyId2x6 = "ALTERNATE-WALL-PLANE-CORE-ASSEMBLY-2X6";
export const alternateWallPlaneCoreAssemblyId2x8 = "ALTERNATE-WALL-PLANE-CORE-ASSEMBLY-2X8";
export const defaultWallPlaneInteriorFinishAssemblyId = "DEFAULT-WALL-PLANE-INTERIOR-FINISH-ASSEMBLY";
export const defaultWallPlaneExteriorFinishAssemblyId = "DEFAULT-WALL-PLANE-EXTERIOR-FINISH-ASSEMBLY";
export const wallPlaneOutwardCladdingAssemblyWallExterior1Id = "WALL-PLANE-EXTERIOR-FINISH-ASSEMBLY-WALLEXTERIOR1";
export const defaultWallPlaneCMUStemWallAssemblyId = "DEFAULT-WALL-PLANE-CMU-STEM-WALL-ASSEMBLY";
export const defaultWallPlaneCIPFootingAssemblyId = "DEFAULT-WALL-PLANE-CIP-STRIP-FOOTING-ASSEMBLY";

export type BuiltInWallPlaneAssemblyId =
  | typeof defaultWallPlaneEmptyAssemblyId
  | typeof defaultWallPlaneCoreAssemblyId
  | typeof alternateWallPlaneCoreAssemblyId2x6
  | typeof alternateWallPlaneCoreAssemblyId2x8
  | typeof defaultWallPlaneInteriorFinishAssemblyId
  | typeof defaultWallPlaneExteriorFinishAssemblyId
  | typeof wallPlaneOutwardCladdingAssemblyWallExterior1Id
  | typeof defaultWallPlaneCMUStemWallAssemblyId
  | typeof defaultWallPlaneCIPFootingAssemblyId;

export const builtInWallPlaneAssemblies: Table<WallPlaneAssemblyEntity> = {
  [defaultWallPlaneEmptyAssemblyId]: wallPlaneAssemblyNew(
    "Empty Wall Plane Assembly",
    {},
    defaultWallPlaneEmptyAssemblyId,
  ),
  [defaultWallPlaneCoreAssemblyId]: wallPlaneAssemblyNew(
    "2x4 16 O.C. Core Assembly",
    {
      "2x4-16-oc-core-framing": {
        id: "2x4-16-oc-core-framing",
        sortIndex: 0,
        name: "Framing",
        layerType: "FRAMING",
        framingComponentId: default2x4Id,
        studSpacing: 16,
        topPlateCount: 1,
        bottomPlateCount: 1,
      },
    },
    defaultWallPlaneCoreAssemblyId,
  ),
  [alternateWallPlaneCoreAssemblyId2x6]: wallPlaneAssemblyNew(
    "2x6 16 O.C. Core Assembly",
    {
      "2x6-16-oc-core-framing": {
        id: "2x6-16-oc-core-framing",
        sortIndex: 0,
        name: "Framing",
        layerType: "FRAMING",
        framingComponentId: default2x6Id,
        studSpacing: 16,
        topPlateCount: 1,
        bottomPlateCount: 1,
      },
    },
    alternateWallPlaneCoreAssemblyId2x6,
  ),
  [alternateWallPlaneCoreAssemblyId2x8]: wallPlaneAssemblyNew(
    "2x8 16 O.C. Core Assembly",
    {
      "2x8-16-oc-core-framing": {
        id: "2x8-16-oc-core-framing",
        sortIndex: 0,
        name: "Framing",
        layerType: "FRAMING",
        framingComponentId: default2x8Id,
        studSpacing: 16,
        topPlateCount: 1,
        bottomPlateCount: 1,
      },
    },
    alternateWallPlaneCoreAssemblyId2x8,
  ),
  [defaultWallPlaneExteriorFinishAssemblyId]: wallPlaneAssemblyNew(
    '7/16" OSB w/ wallExterior0 finish',
    {
      defaultExteriorFinishSheathing: {
        id: "defaultExteriorFinishSheathing",
        sortIndex: 0,
        layerType: "SHEATHING",
        sheathingComponentId: defaultWallExteriorSheathingComponentId,
        finish: { finishType: "EXTERIOR", finishPalette: "wallExterior0" },
      },
    },
    defaultWallPlaneExteriorFinishAssemblyId,
  ),
  [wallPlaneOutwardCladdingAssemblyWallExterior1Id]: wallPlaneAssemblyNew(
    '7/16" OSB w/ wallExterior1 finish',
    {
      alternateExteriorFinishSheathing: {
        id: "alternateExteriorFinishSheathing",
        sortIndex: 0,
        layerType: "SHEATHING",
        sheathingComponentId: defaultWallExteriorSheathingComponentId,
        finish: { finishType: "EXTERIOR", finishPalette: "wallExterior1" },
      },
    },
    wallPlaneOutwardCladdingAssemblyWallExterior1Id,
  ),
  [defaultWallPlaneInteriorFinishAssemblyId]: wallPlaneAssemblyNew(
    '1/2" Drywall w/ wallInterior0 finish',
    {
      defaultInteriorFinishSheathing: {
        id: "defaultInteriorFinishSheathing",
        sortIndex: 0,
        name: "Sheathing",
        layerType: "SHEATHING",
        sheathingComponentId: defaultWallInteriorSheathingComponentId,
        finish: {
          finishType: "INTERIOR",
          finishPalette: "wallInterior0",
        },
      },
    },
    defaultWallPlaneInteriorFinishAssemblyId,
  ),
  [defaultWallPlaneCMUStemWallAssemblyId]: wallPlaneAssemblyNew(
    '8" CMU Stem Wall',
    {
      cmuStemWall: {
        id: "cmuStemWall",
        sortIndex: 0,
        layerType: "MASONRY",
        masonryComponentId: defaultWallMasonryComponentId,
        finish: { finishType: "EXTERIOR", finishPalette: "retainingWall0" },
      },
    },
    defaultWallPlaneCMUStemWallAssemblyId,
  ),
  [defaultWallPlaneCIPFootingAssemblyId]: wallPlaneAssemblyNew(
    '18" Concrete CIP Footing',
    {
      cipConcreteFooting: {
        id: "cipConcreteFooting",
        sortIndex: 0,
        layerType: "CAST_IN_PLACE",
        cipComponentId: defaultWallCIPStripFootingComponentId,
        finish: { finishType: "EXTERIOR", finishPalette: "foundation0" },
      },
    },
    defaultWallPlaneCIPFootingAssemblyId,
  ),
};

export const wallPlaneCoreAssemblyGroupId = "wall-plane-core-assembly-group";
export const wallPlaneFoundationAssemblyGroupId = "wall-plane-foundation-assembly-group";
export const wallPlaneInteriorAssemblyGroupId = "wall-plane-interior-assembly-group";
export const wallPlaneExteriorAssemblyGroupId = "wall-plane-exterior-assembly-group";

export const wallPlaneContentMutableBuiltInGroupIds = [
  wallPlaneCoreAssemblyGroupId,
  wallPlaneInteriorAssemblyGroupId,
  wallPlaneExteriorAssemblyGroupId,
  wallPlaneFoundationAssemblyGroupId,
] as const;

export type WallPlaneContentMutableBuiltInGroupId = (typeof wallPlaneContentMutableBuiltInGroupIds)[number];

export function isWallPlaneContentMutableBuiltInGroupId(
  groupId: string,
): groupId is WallPlaneContentMutableBuiltInGroupId {
  return wallPlaneContentMutableBuiltInGroupIds.some((id) => id === groupId);
}

export function builtInWallPlaneAssemblyGroupForId(assemblyId: BuiltInWallPlaneAssemblyId): string {
  switch (assemblyId) {
    case defaultWallPlaneEmptyAssemblyId:
    case defaultWallPlaneCoreAssemblyId:
    case alternateWallPlaneCoreAssemblyId2x6:
    case alternateWallPlaneCoreAssemblyId2x8:
      return wallPlaneCoreAssemblyGroupId;
    case defaultWallPlaneInteriorFinishAssemblyId:
      return wallPlaneInteriorAssemblyGroupId;
    case defaultWallPlaneExteriorFinishAssemblyId:
    case wallPlaneOutwardCladdingAssemblyWallExterior1Id:
      return wallPlaneExteriorAssemblyGroupId;
    case defaultWallPlaneCMUStemWallAssemblyId:
    case defaultWallPlaneCIPFootingAssemblyId:
      return wallPlaneFoundationAssemblyGroupId;
    default:
      return assertUnreachable(assemblyId);
  }
}

export function defaultWallPlaneAssemblyIdForGroup(groupId: WallPlaneContentMutableBuiltInGroupId): string {
  switch (groupId) {
    case wallPlaneInteriorAssemblyGroupId:
      return defaultWallPlaneInteriorFinishAssemblyId;
    case wallPlaneExteriorAssemblyGroupId:
      return defaultWallPlaneExteriorFinishAssemblyId;
    case wallPlaneCoreAssemblyGroupId:
      return defaultWallPlaneCoreAssemblyId;
    case wallPlaneFoundationAssemblyGroupId:
      return defaultWallPlaneCMUStemWallAssemblyId;
    default:
      return assertUnreachable(groupId);
  }
}

export type WallPlaneAssemblyContentMutableBuiltInDoc = Pick<Doc, "entityGroups" | "wallPlaneAssemblies">;

export function wallPlaneAssemblyContentMutableBuiltInGroupId(
  doc: WallPlaneAssemblyContentMutableBuiltInDoc,
  assemblyId: string,
): string | undefined {
  const fromEntityGroups = doc.entityGroups?.[`wallPlaneAssembly.${assemblyId}`];
  if (fromEntityGroups) {
    return fromEntityGroups;
  }
  if (isBuiltInWallPlaneAssembly(assemblyId)) {
    return builtInWallPlaneAssemblyGroupForId(assemblyId);
  }
  return undefined;
}

export function wallPlaneAssemblyIdsInContentMutableBuiltIn(
  doc: WallPlaneAssemblyContentMutableBuiltInDoc,
  contentMutableBuiltInGroupId: string,
): string[] {
  // TODO WALLS V2: once defaultWallPlaneAssemblies are put in the doc, we can remove the spread here.
  const assemblies = { ...builtInWallPlaneAssemblies, ...doc.wallPlaneAssemblies };

  function compareIds(a: string, b: string): number {
    const aSortIndex = assemblies[a]?.sortIndex ?? 0;
    const bSortIndex = assemblies[b]?.sortIndex ?? 0;
    if (aSortIndex !== bSortIndex) {
      return aSortIndex - bSortIndex;
    }
    return a.localeCompare(b);
  }

  return Object.keys(assemblies)
    .filter(
      (assemblyId) => wallPlaneAssemblyContentMutableBuiltInGroupId(doc, assemblyId) === contentMutableBuiltInGroupId,
    )
    .sort(compareIds);
}

export function sortWallPlaneAssemblyIdsForRuleDropdown(
  doc: Doc,
  assemblyIds: readonly string[],
  preferredGroupId: string,
  mergedAssemblies?: Record<string, WallPlaneAssemblyEntity>,
): string[] {
  const assemblies = mergedAssemblies ?? mergeWallPlaneAssemblies(doc);

  function compareIds(a: string, b: string): number {
    const aPreferred = wallPlaneAssemblyContentMutableBuiltInGroupId(doc, a) === preferredGroupId;
    const bPreferred = wallPlaneAssemblyContentMutableBuiltInGroupId(doc, b) === preferredGroupId;
    if (aPreferred !== bPreferred) {
      return aPreferred ? -1 : 1;
    }
    const aSortIndex = assemblies[a]?.sortIndex ?? 0;
    const bSortIndex = assemblies[b]?.sortIndex ?? 0;
    if (aSortIndex !== bSortIndex) {
      return aSortIndex - bSortIndex;
    }
    return a.localeCompare(b);
  }

  return [...assemblyIds].sort(compareIds);
}

export function isBuiltInWallPlaneAssembly(id: string): id is BuiltInWallPlaneAssemblyId {
  return Object.hasOwn(builtInWallPlaneAssemblies, id);
}

export function mergeWallPlaneAssemblies(doc: Doc): Record<string, WallPlaneAssemblyEntity> {
  // TODO WALLS V2: once defaultWallPlaneAssemblies are put in the doc, we can remove the spread here.
  return { ...builtInWallPlaneAssemblies, ...doc.wallPlaneAssemblies };
}
